import React, { useState, useRef, useEffect, useContext, useMemo } from 'react';
import qs from 'qs';
import { useRouter } from 'next/router';
import { useSWRConfig } from 'swr'
import { ArrowRightCircle  } from 'react-feather';
import toast from 'react-hot-toast';

import {
    Grid,
    Cell,
    Box,
    Container,
    H1,
    PanelKeys,
    Btn,
    ThemeContext,
} from '@saladbob/sassafras'

// Utils
import addLoginCreds from '../../utils/addLoginCreds';

// CRUD
import getSite from '../../crud/getSite';
import getTeam from '../../crud/getTeam';
import getTournamentBySlug from '../../crud/getTournamentBySlug';
import { fetchUser } from '../../hooks/useSwr';

// Providers
import { useTeam, useTeamDispatch } from '../../components/providers/TeamProvider';
import { useUser } from '../../components/providers/UserProvider';
import { useRegistration, useRegistrationDispatch } from '../../components/providers/RegistrationProvider';
import saveTeam from '../../crud/saveTeam';


// Components
import Layout from '../../components/register/Layout';
import TeamCard from '../../components/cards/TeamCard';
import BackBtn from '../../components/buttons/BackBtn';
import AltBtn from '../../components/buttons/AltBtn';
import CheckOut from '../../components/register/CheckOut';

// Steps
import EditTeam from '../../components/register/EditTeam';
import EditPlayer from '../../components/register/EditPlayer';
import VerifyPlayers from '../../components/register/VerifyPlayers';
import Optins from '../../components/register/Optins';
import SpecialDivision from '../../components/register/SpecialDivisions';

// Types
import type { Tournament, Theme, Site, User, Optin } from '../../types';
import type { NextPage } from 'next'
import type { SaveTeam } from '../../crud/saveTeam';

export type PageProps = {
    metaTitle: string,
    tournament: Tournament,
    stripeKey: string,
    site: Site,
    optins: Optin[],
}

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
    getLayout?: (page: React.ReactElement, pageProps?:PageProps) => React.ReactNode,
}

export async function getStaticPaths() {
    return {
        paths: [],
        fallback: 'blocking', // can also be true or 'blocking'
    }
}

export async function getStaticProps({ params }) {
    if (!params.slug || params.slug === 'undefined') {
        return {
            notFound: true,
        }
    }
    const site = await getSite();
    const tournament = await getTournamentBySlug(process.env.NEXT_PUBLIC_API_URL, params.slug);
    const optins = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/optins?where[tournaments][in]=${tournament.id}&depth=0`).then((res) => res.json()).then((data) => data.docs);
    if (!tournament) {
        return {
            notFound: true,
        }
    }

    return {
        props: {
            metaTitle: 'Register',
            tournament,
            optins,
            stripeKey: process.env.STRIPE_PUBLISHABLE_KEY,
            ...site,
        },
    }
}

const Page: NextPageWithLayout<PageProps> = ({tournament, optins: originalOptins, stripeKey }) => {
    const theme = useContext<Theme>(ThemeContext);
    const router = useRouter();
    const user = useUser();
    const { teams } = useRegistration();
    const team = useTeam();
    const teamDispatch = useTeamDispatch();
    const regDispatch = useRegistrationDispatch();
    const [step, setStep] = useState<string | string[]>(router.query.step || (teams && teams.length > 0 ? 'choose' : 'create-team'));
    const [position, setPosition] = useState<number>();
    const [order, setOrder] = useState<{ division?: string, optins?: string[] }>({});
    const subsribeEvents = useRef<any[]>([]);
    const { mutate } = useSWRConfig();
    const submitting = useRef(false);
    // Filter opt-ins to only those that the user has not opted into
    const [optins, setOptins] = useState(originalOptins);

    const defaultTeam = useMemo(() => ({
        color: theme.colors.primary,
        color2: theme.colors.secondary,
        canEdit: true,
        players: [{}, {}, {}, {}],
    }), [theme]);

    const specialDivisions = useMemo(() => {
        if (tournament.specialDivisions) {
            return tournament.specialDivisions.map((div) => ({
                label: div.division,
                value: div.division,
                color: div.color || 'secondary',
                txtColor: div.textColor || 'white',
                coed: div.coed,
            }));
        }
        return null;
    }, [tournament.specialDivisions]);

    // =======================
    // DISPATCH SETTERS
    // =======================

    const setHeading = (heading) => {
        regDispatch({
            type: 'update',
            data: {
                heading
            },
        });
    };

    const setTeam = (t) => {
        teamDispatch({
            type: 'replace-team',
            team: t
        });
    };

    const setPlayer = (p, i) => {
        teamDispatch({
            type: 'replace-player',
            player: p,
            index: i,
        });
    };

    const setPlayers = (p) => {
        teamDispatch({
            type: 'replace-players',
            players: p,
        });
    };

    const setStepParam = (st: string, replace?: boolean) => {
        const qs = { ...router.query };

        if (st === 'create-team') {
            delete qs.teamId;
        }

        if (replace) {
            router.replace(
                {
                    query: { ...qs, step: st },
                },
                undefined,
                { shallow: true }
            );
        } else {
            router.push(
                {
                    query: { ...qs, step: st },
                },
                undefined,
                { shallow: true }
            );
        }
    };

    // ==================
    // EVENTS
    // ==================

    // Add event to be fired on slide enter
    const addEvent = (e) => {
        subsribeEvents.current.push(e);
    }

    const onSlideEntered = () => {
        if (subsribeEvents.current.length > 0) {
            subsribeEvents.current.forEach((e) => {
                if (typeof e === 'function') { e() }
            });
            subsribeEvents.current = [];
        }
    };

    const selectATeam = () => {
        setStepParam('choose-team');
    };

    const chooseTeam = (id?: string) => {
        if (!id) {
            const query = { ...router.query };
            delete query.teamId;

            return router.push(
                {
                    query: { ...router.query, step: 'create-team'},
                },
                undefined,
                { shallow: true }
            );
        }
        router.push(
            {
                query: { ...router.query, step: 'create-team', teamId: id },
            },
            undefined,
            { shallow: true }
        );
    };

    const createTeam = () => {
        addEvent(() => {
            const newTeam = team && team.id ? { ...defaultTeam } : { ...defaultTeam, ...team };
            setTeam(newTeam);
            setPosition(0);
        });
        setStepParam('create-team');
    };

    const swapPlayer = (pos) => {
        setPosition(pos);
        setStepParam('edit-player');
    };

    const addPlayer = (pos, player) => {
        addEvent(() => {
            setPlayer(player, pos);
        });

        setStepParam('create-team');
    };

    const verifyPlayers = async () => {
        if (team.id && !team.canEdit) {
            return setStepParam('divisions');
        }

        if (team && team.players.length < 4) {
            return toast('You must have 4 players to continue');
        }

        const playersToVerify = team.players.reduce((reducer, player) => {
            if (!player.verified) {
                reducer.push(player);
            }
            return reducer;
        }, []);

        if (playersToVerify.length === 0) {
            initSaveToast(team.players);
        } else {
            setStepParam('verify-players');
        }
    };

    // Save team and move to checkout
    const saveMyTeam = async (verifiedPlayers): Promise<SaveTeam> => {
        setPlayers(verifiedPlayers);

        if (user) {
            const newTeam = {
                ...team,
                players: verifiedPlayers,
                manager: {
                    relationTo: 'user',
                    value: user.id,
                },
            };
            const savedTeam = await saveTeam(newTeam, user);

            if ('errors' in savedTeam) {
                return savedTeam;
            }

            return savedTeam;
        }
    };

    // Save team and move to checkout from Verify Teams
    const initSaveToast = async (verifiedPlayers: User[]) => {
        if (!submitting.current) {
            submitting.current = true;
            const saveTeam = new Promise<SaveTeam>(async (resolve, reject) => {
                const checkUser = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/user/me`, {
                    credentials: 'include',
                }).then((res) => res.json());

                if (!checkUser.user) {
                    submitting.current = false;
                    mutate(`${process.env.NEXT_PUBLIC_API_URL}/user/me`);
                    reject([{ message: 'You must be logged in to continue.' }]);
                }

                try {
                    const savedTeam = await saveMyTeam(verifiedPlayers);
                    if ('errors' in savedTeam  && savedTeam.errors && savedTeam.errors.length) {
                        throw savedTeam.errors;
                    }
                    setTeam(savedTeam);
                    return resolve(savedTeam);
                } catch (err) {
                    return reject(err);
                };
            });

            toast.promise(saveTeam, {
                loading: 'Saving Team...',
                success: (team) => {
                    submitting.current = false;
                    localStorage.removeItem('registerPosition');
                    // if special divion options are available, go to special divisions
                    if (('ageDivision' in team && team.ageDivision > 5) || specialDivisions) {
                        setStepParam('divisions');
                    } else if (optins && optins.length > 0) {
                        setStepParam('optins');
                    } else {
                        setStepParam('checkout');
                    }
                    return 'Team Saved';
                },
                error: (err) => {
                    submitting.current = false;
                    setStepParam('create-team', true);
                    if (err[0].player) {
                        return `${err[0].player}: ${err[0].message}`;
                    }
                    return err[0].message || 'An error occurred while saving the team. Please contact customer support if you can\'t resolve the issue.';
                },
            });
        };
    };

    const selectDivision = (division) => {
        setOrder({ ...order, division });
        setStepParam(optins && optins.length > 0 ? 'optins' :'checkout');
    };

    const registerOptins = (optins: string[]) => {
        setOrder({ ...order, optins });
        setStepParam('checkout');
    };

    const onPaymentComplete = () => {
        //
    };

    // ==================
    // EFFECTS
    // ==================

    // Set step from query string
    useEffect(() => {
        const qsStep = router.query.step;

        if (qsStep) {
            setStep(router.query.step);
        }
    }, [router.query.step]);

    // Fetch team from query string
    useEffect(() => {
        const teamID = router.query.teamId;
        const qsStep = router.query.step;

        const loadTeam = async (id: string) => {
            const resTeam = await getTeam(id);
            setTeam(resTeam);
        };

        // If teamID and step is create-team, load team
        if (typeof teamID === 'string' && qsStep !== 'choose-team' && teamID !== team?.id) {
            loadTeam(teamID);
        // If no teamID and step is create-team, create new team
        } else if (teamID && (qsStep == 'choose-team' || qsStep == 'choose')) {
            const qs = { ...router.query };
            delete qs.teamId;
            router.replace(
                {
                    query: { ...router.query },
                },
                undefined,
                { shallow: true }
            );
        }
    }, [router.query.step, router.query.teamId]);

    useEffect(() => {
        switch (step) {
            case 'choose': {
                setHeading('Choose Your Team');
                break;
            }
            case 'choose-team': {
                setHeading('Select Team');
                break;
            }
            case 'create-team': {
                if (team && team.name) {
                    setHeading(team.name);
                } else {
                    setHeading('Create Team');
                }
                break;
            }
            case 'edit-player': {
                if (team && team.name) {
                    setHeading(`${team.name} > Edit Player`);
                } else {
                    setHeading('Create Team > Edit Player');
                }
                break;
            }
            case 'verify-players': {
                setHeading('Verify Players');
                break;
            }
            case 'divisions': {
                setHeading('Select a Division');
                break;
            }
            case 'optins': {
                setHeading('Opportunities');
                break;
            }
            case 'checkout': {
                setHeading('Checkout');
                break;
            }
        }
    }, [step]);

    // Store selected position for reloading
    useEffect(() => {
        if (typeof position === 'number') {
            localStorage.setItem('registerPosition', `${position}`);
        }
    }, [position]);

    // Remove optins that the user has already opted into
    useEffect(() => {
        const filterOptins = async () => {
            if (user && originalOptins && originalOptins.length > 0) {
                const query = qs.stringify({
                    depth: 0,
                    where: {
                        id: {
                            in: originalOptins.map((optin) => optin.id),
                        },
                        users: {
                            not_in: [user.id],
                        },
                    },
                }, { addQueryPrefix: true });
                const filteredOptins = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/optins${query}`).then((res) => res.json()).then((data) => data.docs);
                setOptins(filteredOptins);
            }
        };

        filterOptins();
    }, [user, originalOptins]);

    // Set empty team on init
    useEffect(() => {
        const { step: qsStep, teamId } = router.query;
        const storedPosition = localStorage.getItem('registerPosition');
        setPosition(Number(storedPosition|| 0));

        if (!teamId) {
            setTeam(defaultTeam);
        }

        if (teams && teams.length === 0) {
            setStepParam('create-team', true);
        // no step defined in qs
        } else if (!qsStep) {
            setStepParam('choose', true);
        }
    }, []);

    if (!team) {
        return null;
    }

    if (tournament.status !== 'open') {
        <Container size="lg" height="100%" verticalAlign="center" align="center">
            <H1 font="leader" weight="normal" color="neutral" mb="xl">
                {tournament.title} is not open for registration.
            </H1>
        </Container>
    }

    return (
        <>
            <Container size="lg">
                <PanelKeys step={step} travel={30} onComplete={onSlideEntered}>
                    <Box key="choose">
                        <Grid bgColor="white" columns={{ default: '1fr', laptop: '1fr 1fr' }} gridGap="lg">
                            <Box verticalAlign="center" justify="end">
                                <Btn size="lg" font="leader" color="secondary" full onClick={selectATeam}>
                                    Choose Existing Team
                                </Btn>
                            </Box>
                            <Box verticalAlign="center" justify="end">
                                <Btn size="lg" font="leader" full onClick={createTeam}>
                                    Create New Team
                                </Btn>
                            </Box>
                            { team?.name && (
                                <Cell column="2/2" justify="end">
                                    <AltBtn
                                        size="lg"
                                        iconRight={<ArrowRightCircle size="24" />}
                                        onClick={() => setStepParam('create-team')}
                                    >
                                        Continue With {team.name}
                                    </AltBtn>
                                </Cell>
                            )}
                        </Grid>
                    </Box>
                    <Box key="choose-team">
                        {teams && teams.length === 0 && (
                            <H1 font="leader" weight="normal" color="neutral" mb="xl">
                                You have no teams. Create one to continue.
                            </H1>
                        )}
                        {teams && teams.length > 0 && (
                            <Grid
                                columns={{ phone: '1fr', laptop: 'repeat(auto-fit, minMax(400px, 1fr))' }}
                                gridGap={{ phone: 'md', laptop: 'lg' }}
                                mt={{ phone: 0, laptop: 'xl' }}
                                mb={{ phone: 'md', laptop: 'xxl'}}
                            >
                                {teams && teams.length > 0 && teams.map((team) => (
                                    <TeamCard team={team} onClick={() => chooseTeam(team.id)} />
                                ))}
                            </Grid>
                        )}

                        <Btn outline color="secondary" onClick={createTeam}>
                            Create New Team
                        </Btn>
                    </Box>
                    <Box key="create-team">
                        <EditTeam
                            team={team}
                            setTeam={setTeam}
                            onSubmit={verifyPlayers}
                            swapPlayer={swapPlayer}
                            submitLabel="Continue"
                        />
                        {teams && teams.length > 0 && (
                            <BackBtn onClick={() => {setStepParam('choose-team')}} mt="md"/>
                        )}
                    </Box>
                    <Box key="edit-player">
                        <EditPlayer position={position} tournamentID={tournament.id} onSubmit={addPlayer} />
                    </Box>
                    <Box key="verify-players">
                        <VerifyPlayers onSubmit={initSaveToast} onExit={() => setStepParam('create-team')}/>
                    </Box>
                    <Box key="divisions">
                        <SpecialDivision
                            divisions={specialDivisions}
                            onSubmit={selectDivision}
                            onExit={() => setStepParam('create-team')} 
                        >
                            <BackBtn onClick={() => { setStepParam('create-team') }} mt="md" />
                        </SpecialDivision>
                    </Box>
                    <Box key="optins">
                        <Optins
                            tournamentTitle={tournament.title}
                            optins={optins}
                            onSubmit={registerOptins}
                            onExit={() => setStepParam('create-team')} 
                        >
                            <BackBtn onClick={() => { setStepParam(('ageDivision' in team && team.ageDivision > 5) || specialDivisions ? 'divisions' : 'create-team') }} mt="md" />
                        </Optins>
                    </Box>
                    <Box key="checkout">
                        <CheckOut
                            tournament={tournament}
                            team={team}
                            stripeKey={stripeKey}
                            backHandler={() => setStepParam('choose')}
                            onConfirm={onPaymentComplete}
                            orderMeta={order}
                        />
                    </Box>
                </PanelKeys>
            </Container>
        </>
    );
};

Page.getLayout = function getLayout(page: React.ReactElement, pageProps: PageProps ) {
    return (
        <Layout { ...pageProps }>
            {page}
        </Layout>
    )
}

export default Page;